'use client';
import { useEffect, useRef, useState } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import Image from 'next/image';
import Layout from '../Components/Layout/Layout';
import MobileAbout from './components/MobileAbout';
import styles from './about.module.css';
import CartTicket from '../Components/Navbar/CartTicket';

gsap.registerPlugin(ScrollTrigger);

export default function About() {
  const [isMobile, setIsMobile] = useState(false);
  const heroTextRef = useRef(null);
  const subtitleRef = useRef(null);
  const imageRef = useRef(null);
  const containerRef = useRef(null);

  const products = [
    {
      src: 'https://longevity20155.sirv.com/zinc/1.webp',
      name: 'ZINC',
    },
    {
      src: 'https://longevity20155.sirv.com/minci/1.webp',
      name: 'MINCI',

    },
    {
      src: 'https://longevity20155.sirv.com/testo/1.webp',
      name: 'TESTO',
    },
    {
      src: 'https://longevity20155.sirv.com/zinc/1.webp',
      name: 'FERTI',
    },
  ];

  useEffect(() => {
    // Check if mobile
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 800);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);

    // Only run animations on desktop
    if (window.innerWidth > 800) {
      // Split text into characters
      const blurTextEl = document.getElementById('blurText');
      if (blurTextEl) {
        const text = blurTextEl.textContent;
        blurTextEl.innerHTML = text.split('').map((char, i) =>
          char === ' ' ? ' ' : `<span class="char-${i}" style="display: inline-block;">${char}</span>`
        ).join('');
      }

      const ctx = gsap.context(() => {
        gsap.to("#navbar", { autoAlpha: 1 });

        // Hero text scale animation
        const tl = gsap.timeline({
          scrollTrigger: {
            trigger: containerRef.current,
            start: 'top top',
            scrub: 1,
            pin: true,
            anticipatePin: true
          }
        })
        tl.to(heroTextRef.current, {
          scale: 5,
          autoAlpha: 0,
          duration: 1
        }, 'a')
          .to(imageRef.current, {
            scale: 0.5,
            autoAlpha: 0,
            duration: 1
          }, 'a')

          // Third animation - starts at same time but lasts 2 seconds
          // So the first two finish at the middle of this one
          .to('.' + styles.left, {
            transform: "translate3d(150%,0,0)",
            duration: 2
          }, 'a')
          .to('.' + styles.right, {
            transform: "translate3d(-150%,0,0)",
            duration: 2
          }, 'a')
          .to("." + styles.blurTextContainer, { autoAlpha: 1, duration: .3 }, "-=1")
          // Blur text animation - starts after the above animations
          .fromTo('#blurText span[class^="char-"]', {
            scaleY: 0.1,
            scaleX: 1.8,
            filter: 'blur(10px) brightness(50%)',
            willChange: 'filter, transform',
          }, {
            scaleY: 1,
            scaleX: 1,
            filter: 'blur(0px) brightness(100%)',
            stagger: 0.02,
            duration: 2,
            ease: 'power2.out'
          }, "-=1")
          .to("." + styles.blurTextContainer, { transform: "translate3d(0,-100%,0)", autoAlpha: 0 }, "z")
          // About section slides up from bottom
          .to("." + styles.aboutSection, {
            transform: 'translate3d(0,0,0)', autoAlpha: 1,
            ease: 'power2.out'
          }, 'z');
      });



      return () => {
        ctx.revert();
        window.removeEventListener('resize', checkMobile);
      };
    }

    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Show mobile version for small screens
  if (isMobile) {
    return (
      <Layout key={'mobile'}>
        <MobileAbout />
        <CartTicket />
      </Layout>
    );
  }

  // Desktop version with animations
  return (
    <Layout>
      <div ref={containerRef} className={styles.aboutContainer}>
        <div className={styles.heroSection}>
          {/* Background product image */}
          <div ref={imageRef} className={styles.backgroundImage}>
            <Image
              src="https://longevity20155.sirv.com/OMEGA.webp"
              alt="OMEGA Product"
              fill
              style={{ objectFit: 'contain' }}
              unoptimized
            />
          </div>

          {/* Main hero text */}
          <h1 ref={heroTextRef} className={styles.heroText}>
            Logvplus
          </h1>

          {/* Cycling subtitle */}
          <div ref={subtitleRef} className={styles.subtitle}>
            <CyclingText />
          </div>

          {/* Blur text effect */}
          <div className={styles.blurTextContainer}>
            <p className={styles.blurText} id="blurText">
              Science-backed supplements crafted with premium ingredients to support your journey towards optimal health, vitality, and longevity through natural wellness solutions.
            </p>
          </div>

          {/* About section with image */}
          <div className={styles.aboutSection}>
            <div className={styles.aboutContent}>

              <div className={styles.aboutText} >
                <p>Chez logvplus, nous croyons que la santé et le bien-être commencent par une nutrition optimale. Cest pourquoi nous développons des compléments alimentaires innovants et de haute qualité.

                </p>

              </div>
              <div className={styles.aboutImageContainer}>
                <h2 className={styles.aboutTitle}>About</h2>


              </div>
            </div>
          </div>

          {/* Product images that appear on scroll */}
          <div className={styles.productsContainer + " " + styles.left}>
            {products.map((product, i) => (
              <div
                key={i}
                className={`${styles.productCard} product-${i}`}
                style={{

                  background: product.color
                }}
              >
                <div className={styles.productImageWrapper}>
                  <Image
                    src={product.src}
                    alt={product.name}
                    fill
                    style={{ objectFit: 'contain' }}
                    unoptimized
                  />
                </div>
                <div className={styles.productOverlay}>
                  <h3>{product.name}</h3>
                </div>
              </div>
            ))}
          </div>
          <div className={styles.productsContainer + " " + styles.right}>
            {products.map((product, i) => (
              <div
                key={i}
                className={`${styles.productCard} product-${i}`}
                style={{

                  background: product.color
                }}
              >
                <div className={styles.productImageWrapper}>
                  <Image
                    src={product.src}
                    alt={product.name}
                    fill
                    style={{ objectFit: 'contain' }}
                    unoptimized
                  />
                </div>
                <div className={styles.productOverlay}>
                  <h3>{product.name}</h3>
                </div>
              </div>
            ))}
          </div>

        </div>


      </div>
      <CartTicket />

    </Layout>
  );
}

function CyclingText() {
  const textRef = useRef(null);
  const texts = [
    'PREMIUM SUPPLEMENTS',
    'SCIENCE-BACKED FORMULAS',
    'OPTIMAL HEALTH',
    'LONGEVITY FOCUSED'
  ];

  useEffect(() => {
    if (!textRef.current) return;

    const ctx = gsap.context(() => {
      const tl = gsap.timeline({ repeat: -1 });

      texts.forEach((text, i) => {
        tl.set(textRef.current, { textContent: text })
          .to(textRef.current, {
            opacity: 1,
            y: 0,
            duration: 0.8,
            ease: 'power2.out'
          })
          .to(textRef.current, {
            opacity: 0,
            y: -20,
            duration: 0.8,
            delay: 2,
            ease: 'power2.in'
          });
      });
    });

    return () => ctx.revert();
  }, []);

  return (
    <p ref={textRef} className={styles.cyclingText} style={{ opacity: 1 }}>
      PREMIUM SUPPLEMENTS
    </p>
  );
}
